import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, axiosInstance, API } from '../App';
import { ArrowLeft, FileText, Eye, EyeOff, Lightbulb, Check, Loader2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { openSignupPreview } from '../utils/draftPreview';
import axios from 'axios';
import LanguageToggle from '../components/LanguageToggle';
import { useFlags } from '../hooks/useFlags';
import OpeningHoursInput from '../components/OpeningHoursInput';
import { tf } from '../utils/translateFeature';
import EmailTakenNotice from '../components/EmailTakenNotice';
import { formatPhone } from '../utils/phoneFormat';

const BusinessSignUp = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useAuth();
  const flags = useFlags();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [categories, setCategories] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [plans, setPlans] = useState([]);
  // New membership tiers (Free / Basic / Business Plus) from the public API.
  const [membershipTiers, setMembershipTiers] = useState([]);
  const [paidNotReady, setPaidNotReady] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  // Iter 167 — analytics-only WhatsApp tracking. Captured from `?wa=1` on the
  // affiliate WhatsApp share link. Forwarded to the founders-signup payload
  // so the helper dashboard can count WhatsApp-driven signups separately.
  const [viaWhatsapp, setViaWhatsapp] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  // Duplicate-listing warning state (soft check before user advances past Step 2).
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [duplicateChecking, setDuplicateChecking] = useState(false);
  // Iter 212 — Early email-availability gate. Catches both registered users
  // and seeded business listings *before* the applicant invests effort.
  const [emailStatus, setEmailStatus] = useState('idle'); // 'idle'|'checking'|'taken'|'available'
  const [emailTakenReason, setEmailTakenReason] = useState(null);
  const emailInputRef = React.useRef(null);
  const checkEmailAvailability = async (raw) => {
    const email = (raw || '').trim().toLowerCase();
    if (!email || !email.includes('@')) { setEmailStatus('idle'); setEmailTakenReason(null); return null; }
    setEmailStatus('checking');
    try {
      const r = await axios.get(`${API}/auth/check-email`, { params: { email } });
      const ok = !!r.data?.available;
      setEmailStatus(ok ? 'available' : 'taken');
      setEmailTakenReason(ok ? null : (r.data?.reason || 'user_exists'));
      return ok;
    } catch {
      setEmailStatus('idle');
      setEmailTakenReason(null);
      return null;
    }
  };
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    businessName: '',
    description: '',
    category: '',
    customCategory: '',
    address: '',
    city: '',
    country: '',
    region: '',
    province: '',
    localArea: '',
    postcode: '',
    phone: '',
    businessEmail: '',
    website: '',
    openingHours: '',
    groupSizes: [],
    eventTypes: [],
    customEventType: '',
    venueType: [],
    additionalFeatures: [],
    customFeatureText: '',
    accessibilityFeatures: [],
    dietaryOptions: [],
    planId: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  // Hybrid 60-day free trial (12-month plan only). Defaults ON when 12-month
  // is selected; user can opt-out (e.g. if they already know they want to be
  // billed immediately). Hidden entirely for Basic plan.
  // Default ON when the trial feature is enabled; ignored entirely when
  // disabled (see useEffect below that forces it off).
  const [useTrialOnSignup, setUseTrialOnSignup] = useState(true);
  // Iter 192 — Hard duplicate-guard 409 state. When the server blocks signup
  // with a near-duplicate match, we surface the match inline and let the
  // owner explicitly confirm "this is a different business" to retry.
  const [duplicateBlock, setDuplicateBlock] = useState(null);
  // Stage 2A — worldwide country list for the location selector.
  const [countries, setCountries] = useState([]);
  useEffect(() => {
    axios.get(`${API}/geo/countries`).then((r) => setCountries(r.data?.countries || [])).catch(() => setCountries([]));
  }, []);

  const totalSteps = 6;

  // ----- New membership model helpers -----
  const PAID_PLAN_IDS = ['basic_monthly', 'plus_monthly', 'plus_annual'];
  const isFreePlan = formData.planId === 'free';
  const isPaidPlan = PAID_PLAN_IDS.includes(formData.planId);
  const selectedTier = membershipTiers.find((tt) => tt.id === formData.planId) || null;
  const fmtPrice = (n) => `€${Number(n || 0).toFixed(2)}`;

  const selectPlanAndContinue = (planId) => {
    setFormData((f) => ({ ...f, planId }));
    setPaidNotReady(false);
    setError('');
    setStep(1);
  };

  // Localized feature bullets / tier key helpers for the membership screen.
  const tierKeyFor = (planId) => planId === 'free' ? 'free' : planId === 'basic_monthly' ? 'basic' : 'plus';
  const planFeatures = (tierKey) => {
    const arr = t(`membership.feat.${tierKey}`, { returnObjects: true });
    return Array.isArray(arr) ? arr : null;
  };

  useEffect(() => {
    fetchCategories();
    fetchFilterOptions();
    fetchPlans();
    fetchMembershipTiers();
    // Read referral code from URL (only when the affiliate system is enabled —
    // with flag off /api/referral/validate returns 404, so we skip cleanly).
    const refCode = searchParams.get('ref');
    if (refCode && flags.affiliatesEnabled) {
      setReferralCode(refCode.toUpperCase());
      axios.get(`${API}/referral/validate/${refCode}`).then(res => {
        if (res.data.valid) setReferralValid(res.data.helperName);
      }).catch(() => {});
    }
    // Iter 167 — pick up the WhatsApp tracking flag (analytics only).
    if (searchParams.get('wa') === '1') {
      setViaWhatsapp(true);
    }
    // Force trial-toggle off whenever the free trial feature is disabled — this
    // prevents the submit branch from ever POSTing to /stripe/start-trial.
    if (flags._loaded && !flags.freeTrialEnabled && useTrialOnSignup) {
      setUseTrialOnSignup(false);
    }
  }, [flags.affiliatesEnabled, flags.freeTrialEnabled, flags._loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Soft duplicate-listing check: debounce the business-name input and ask the
  // backend if a similar listing already exists. Surfaces a warning + "Claim
  // this business instead" CTA so prospective signups don't create duplicates
  // of seeded or already-listed businesses. Never blocks; user can proceed.
  useEffect(() => {
    const name = (formData.businessName || '').trim();
    if (name.length < 3) {
      setDuplicateMatches([]);
      return;
    }
    setDuplicateChecking(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/businesses/check-duplicate`, {
          params: { name, ...(formData.city ? { city: formData.city } : {}) },
        });
        setDuplicateMatches(res.data?.matches || []);
      } catch {
        setDuplicateMatches([]);
      } finally {
        setDuplicateChecking(false);
      }
    }, 450);
    return () => clearTimeout(handle);
  }, [formData.businessName, formData.city]);

  const fetchMembershipTiers = async () => {
    try {
      const response = await axios.get(`${API}/membership/tiers`);
      setMembershipTiers(response.data?.tiers || []);
    } catch (error) {
      console.error('Error fetching membership tiers:', error);
    }
  };

  const fetchCategories = async () => {    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/filters`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Basic plan re-enabled (Iter 110B) after config.py was changed to hardcode
  // LIVE Stripe Price IDs regardless of env vars — production env-var lock
  // no longer blocks Basic. To temporarily hide again: set this to false.
  const BASIC_PLAN_ENABLED = true;

  // 12-months plan re-enabled (Iter 110G, Feb 2026) after the corrected €70
  // Price ID (`price_1TPiZOL24iN2SxGdGTWR9SKS`) was added. The previous €79
  // mismatch was archived in Stripe.
  const TWELVE_MONTH_PLAN_ENABLED = true;

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/stripe/plans`);
      const all = response.data.plans || [];
      let visible = BASIC_PLAN_ENABLED ? all : all.filter(p => p.id !== 'basic');
      if (!TWELVE_MONTH_PLAN_ENABLED) visible = visible.filter(p => p.id !== '12months');
      setPlans(visible);
      // If a pre-filled planId is hidden, clear it so the user re-picks
      if ((!BASIC_PLAN_ENABLED && formData.planId === 'basic') ||
          (!TWELVE_MONTH_PLAN_ENABLED && formData.planId === '12months')) {
        setFormData(f => ({ ...f, planId: '' }));
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email || !formData.password) {
      setError(t('allFieldsRequired'));
      return;
    }
    if (formData.password.length < 6) {
      setError(t('passwordMinLength'));
      return;
    }
    if (!consentGiven) {
      setError(t('consentRequired', 'You must agree to the Terms & Conditions and Privacy Policy'));
      return;
    }
    // Iter 212 — Hard email availability gate. If the user hasn't yet
    // triggered the onBlur check (or the cached status isn't 'available'),
    // run it now and block progression when the email is taken.
    if (emailStatus !== 'available') {
      const ok = await checkEmailAvailability(formData.email);
      if (ok === false) {
        setError(t('emailAlreadyRegistered', 'This email address is already registered.'));
        return;
      }
    }
    setError('');
    setStep(2);
  };

  const handleStep2Submit = (e) => {
    e.preventDefault();
    if (!formData.businessName || !formData.description) {
      setError(t('businessNameDescRequired'));
      return;
    }
    setError('');
    setStep(3);
  };

  const handleStep3Submit = (e) => {
    e.preventDefault();
    if (!formData.category || !formData.city || !formData.country || !formData.address || !formData.phone || !formData.businessEmail) {
      setError(t('fillAllRequired'));
      return;
    }
    if (formData.category === 'Other' && !formData.customCategory) {
      setError(t('specifyCategoryOther'));
      return;
    }
    setError('');
    setStep(4);
  };

  const handleStep4Submit = (e) => {
    e.preventDefault();
    setError('');
    setStep(5);
  };

  const handleStep5Submit = (e) => {
    e.preventDefault();
    setError('');
    setStep(6);
  };

  const handleFinalSubmit = async (e, opts = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!waiverAccepted) {
      setError(t('waiverRequired', 'Please acknowledge immediate listing activation to continue.'));
      return;
    }
    setError('');
    setDuplicateBlock(null);
    setLoading(true);

    try {
      let websiteUrl = formData.website;
      if (websiteUrl && !websiteUrl.startsWith('http://') && !websiteUrl.startsWith('https://')) {
        websiteUrl = 'https://' + websiteUrl;
      }

      const finalCategory = formData.category === 'Other' && formData.customCategory
        ? formData.customCategory
        : formData.category;

      let finalEventTypes = [...formData.eventTypes];
      if (finalEventTypes.includes('Other') && formData.customEventType) {
        finalEventTypes = finalEventTypes.filter(e => e !== 'Other');
        finalEventTypes.push(formData.customEventType);
      }

      let finalAdditionalFeatures = [...formData.additionalFeatures];
      if (finalAdditionalFeatures.includes('Other') && formData.customFeatureText) {
        finalAdditionalFeatures = finalAdditionalFeatures.filter(f => f !== 'Other');
        finalAdditionalFeatures.push(formData.customFeatureText);
      } else {
        finalAdditionalFeatures = finalAdditionalFeatures.filter(f => f !== 'Other');
      }

      const payload = {
        fullName: formData.fullName,
        email: formData.email,
        password: formData.password,
        businessName: formData.businessName,
        description: formData.description,
        category: finalCategory,
        address: formData.address,
        city: formData.city,
        country: formData.country,
        region: formData.region || null,
        province: formData.province || null,
        town: formData.city,
        localArea: formData.localArea || null,
        postcode: formData.postcode || null,
        phone: formData.phone,
        businessEmail: formData.businessEmail,
        website: websiteUrl || null,
        openingHours: formData.openingHours || null,
        groupSizes: formData.groupSizes,
        dietaryOptions: formData.dietaryOptions,
        eventTypes: finalEventTypes,
        venueType: formData.venueType,
        additionalFeatures: finalAdditionalFeatures,
        accessibilityFeatures: formData.accessibilityFeatures,
        planId: formData.planId || 'free',
        originUrl: window.location.origin,
        consentGiven: consentGiven,
        withdrawalWaiverAccepted: waiverAccepted,
        referralCode: referralCode || null,
        viaWhatsapp: viaWhatsapp,
        confirmedNoDuplicate: !!opts.confirmedNoDuplicate,
      };

      // ----- PAID memberships (Basic / Business Plus) -----
      // Create a Stripe Checkout subscription with a 60-day trial. In Preview,
      // the Stripe Price IDs are not configured yet (checkoutReady === false),
      // so we never hit Stripe — we show a clear notice instead. No real charge.
      if (isPaidPlan) {
        if (!selectedTier?.checkoutReady) {
          setPaidNotReady(true);
          setLoading(false);
          return;
        }
        const res = await axios.post(`${API}/stripe/create-business-checkout`, payload);
        window.location.assign(res.data.checkoutUrl);
        return;
      }

      // ----- FREE Directory (no card, no trial) -----
      // Any non-paid selection (free, or an empty/invalid planId) uses free-signup.
      // The legacy 'founders-signup' endpoint is retired (returns 410) and must
      // never be reached, so we no longer fall back to it.
      await axios.post(`${API}/business/free-signup`, payload);

      // The listing now exists. Auto-login so the owner lands straight on the
      // dashboard. If auto-login fails for any reason (e.g. a mobile browser
      // that blocks storage), the account is still created — send them to
      // sign in with a clear explanation instead of bouncing silently to the
      // login screen with no context.
      try {
        const loginRes = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password,
        });
        const accessToken = loginRes.data?.access_token || loginRes.data?.accessToken;
        const userId = loginRes.data?.user_id || loginRes.data?.userId;
        if (!accessToken) throw new Error('No access token returned from login');
        login(accessToken, 'business', userId);
        window.location.assign('/business/dashboard?free=activated');
      } catch (loginErr) {
        console.error('Auto-login after free signup failed:', loginErr);
        navigate('/signin', {
          replace: true,
          state: {
            from: '/business/dashboard',
            email: formData.email,
            notice: t(
              'autoLoginFailedNotice',
              'Your Free Directory listing was created successfully. Please sign in with the email and password you just chose to open your Business Dashboard.'
            ),
          },
        });
      }
      return;
    } catch (err) {
      console.error('Founders signup error:', err);
      // Iter 192 — surface the duplicate-listing 409 inline so the user can
      // either claim the match or confirm "this is a different business".
      if (err.response?.status === 409 && err.response?.data?.detail?.error === 'duplicate_business') {
        setDuplicateBlock(err.response.data.detail);
        setError('');
        setLoading(false);
        return;
      }
      const errorMessage = err.response?.data?.detail || t('registrationFailed');
      if (typeof errorMessage === 'string' && errorMessage.includes('Email already registered')) {
        setError(t('emailAlreadyRegistered'));
      } else if (typeof errorMessage === 'string') {
        setError(errorMessage);
      } else {
        setError(t('registrationFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleArrayField = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  const isSelected = (field, value) => (formData[field] || []).includes(value);

  const renderTagButton = (field, value) => (
    <button
      key={value}
      type="button"
      onClick={() => toggleArrayField(field, value)}
      className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
        isSelected(field, value)
          ? 'bg-purple-600 text-white'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
      data-testid={`tag-${field}-${value.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
    >
      {isSelected(field, value) && <Check className="h-4 w-4" />}
      {tf(t, value)}
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-600 to-purple-800">
      <header className="p-4 flex justify-between items-center">
        <button
          onClick={() => step <= 0 ? navigate('/') : setStep(step - 1)}
          className="flex items-center text-white"
        >
          <ArrowLeft className="h-6 w-6 mr-2" />
        </button>
        <LanguageToggle />
      </header>

      <div className="flex flex-col items-center px-6 py-4 pb-12">
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-6 md:p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-purple-100 rounded-full mb-3">
              <FileText className="h-7 w-7 text-purple-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="business-signup-title">
              {t('joinAsBusinessTitle')}
            </h1>
            <p className="text-purple-600 font-medium text-sm">
              {t('startInstantlyTagline', 'Start listing your business instantly')}
            </p>
          </div>

          {/* Progress indicator (hidden on the plan-selection screen) */}
          {step >= 1 && (
            <>
              <div className="flex justify-center gap-1.5 mb-6">
                {[...Array(totalSteps)].map((_, i) => (
                  <div key={i} className={`h-1.5 w-12 rounded-full transition-colors ${step > i ? 'bg-purple-600' : 'bg-gray-200'}`} />
                ))}
              </div>
              <p className="text-center text-xs text-gray-500 mb-6">{t('stepOf', { step, total: totalSteps })}</p>
            </>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Step 0: Membership selection */}
          {step === 0 && (
            <div className="space-y-5" data-testid="membership-selection">
              <div className="text-center -mt-2">
                <h2 className="text-lg font-semibold text-gray-900">{t('membership.chooseTitle', 'Choose your membership')}</h2>
                <p className="text-sm text-gray-600 mt-1">{t('membership.chooseSubtitle', 'Start free, or unlock your full profile. Prices include IVA.')}</p>
              </div>

              {membershipTiers.length === 0 && (
                <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-purple-600" /></div>
              )}

              {(() => {
                const tFree = membershipTiers.find((x) => x.id === 'free');
                const tBasic = membershipTiers.find((x) => x.id === 'basic_monthly');
                const tPlusM = membershipTiers.find((x) => x.id === 'plus_monthly');
                const tPlusY = membershipTiers.find((x) => x.id === 'plus_annual');
                const Feature = ({ children }) => (
                  <li className="flex items-start gap-1.5 text-sm text-gray-700">
                    <Check className="h-4 w-4 text-purple-600 shrink-0 mt-0.5" />{children}
                  </li>
                );
                return (
                  <div className="space-y-4">
                    {/* FREE */}
                    {tFree && (
                      <div className="rounded-2xl border border-gray-200 p-5" data-testid="tier-card-free">
                        <div className="flex items-baseline justify-between">
                          <h3 className="text-base font-bold text-gray-900">{t('membership.freeName', 'Free Directory')}</h3>
                          <span className="text-lg font-bold text-gray-900">€0</span>
                        </div>
                        <p className="text-xs text-gray-500 mb-3">{t('membership.freeForever', 'Free forever · no card needed')}</p>
                        <ul className="space-y-1.5 mb-4">{(planFeatures('free') || tFree.publicFeatures || []).map((f) => <Feature key={f}>{f}</Feature>)}</ul>
                        <button type="button" onClick={() => selectPlanAndContinue('free')}
                          className="w-full py-2.5 rounded-lg border-2 border-purple-600 text-purple-700 font-semibold hover:bg-purple-50 transition-colors"
                          data-testid="choose-free-btn">
                          {t('membership.chooseFree', 'Choose Free Directory')}
                        </button>
                      </div>
                    )}

                    {/* BASIC */}
                    {tBasic && (
                      <div className="rounded-2xl border border-gray-200 p-5" data-testid="tier-card-basic">
                        <div className="flex items-baseline justify-between">
                          <h3 className="text-base font-bold text-gray-900">{t('membership.basicName', 'Basic')}</h3>
                          <span className="text-lg font-bold text-gray-900">{fmtPrice(tBasic.amount)}<span className="text-xs font-normal text-gray-500">/{t('membership.month', 'month')}</span></span>
                        </div>
                        <p className="text-xs text-emerald-700 font-medium mb-1">{t('membership.trial60', 'First 60 days free')}</p>
                        <p className="text-[11px] text-gray-500 mb-3">{t('membership.ivaIncluded', 'IVA included')}</p>
                        <ul className="space-y-1.5 mb-4">{(planFeatures('basic') || tBasic.publicFeatures || []).map((f) => <Feature key={f}>{f}</Feature>)}</ul>
                        <button type="button" onClick={() => selectPlanAndContinue('basic_monthly')}
                          className="w-full py-2.5 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition-colors"
                          data-testid="choose-basic-btn">
                          {t('membership.chooseBasic', 'Choose Basic')}
                        </button>
                      </div>
                    )}

                    {/* BUSINESS PLUS */}
                    {tPlusM && (
                      <div className="rounded-2xl border-2 border-purple-400 bg-purple-50/40 p-5" data-testid="tier-card-plus">
                        <div className="flex items-baseline justify-between">
                          <h3 className="text-base font-bold text-gray-900">{t('membership.plusName', 'Business Plus')}</h3>
                          <span className="text-lg font-bold text-gray-900">{fmtPrice(tPlusM.amount)}<span className="text-xs font-normal text-gray-500">/{t('membership.month', 'month')}</span></span>
                        </div>
                        <p className="text-xs text-gray-600 mb-1">{tPlusY ? `${t('membership.orAnnual', 'or')} ${fmtPrice(tPlusY.amount)}/${t('membership.year', 'year')}` : ''}</p>
                        <p className="text-xs text-emerald-700 font-medium mb-1">{t('membership.trial60', 'First 60 days free')}</p>
                        <p className="text-[11px] text-gray-500 mb-3">{t('membership.ivaIncluded', 'IVA included')}</p>
                        <ul className="space-y-1.5 mb-4">{(planFeatures('plus') || tPlusM.publicFeatures || []).map((f) => <Feature key={f}>{f}</Feature>)}</ul>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <button type="button" onClick={() => selectPlanAndContinue('plus_monthly')}
                            className="w-full py-2.5 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition-colors text-sm"
                            data-testid="choose-plus-monthly-btn">
                            {t('membership.choosePlusMonthly', 'Monthly')} · {fmtPrice(tPlusM.amount)}
                          </button>
                          {tPlusY && (
                            <button type="button" onClick={() => selectPlanAndContinue('plus_annual')}
                              className="w-full py-2.5 rounded-lg bg-purple-800 text-white font-semibold hover:bg-purple-900 transition-colors text-sm"
                              data-testid="choose-plus-annual-btn">
                              {t('membership.choosePlusAnnual', 'Annual')} · {fmtPrice(tPlusY.amount)}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    <p className="text-[11px] text-gray-500 text-center">
                      {t('membership.consentNote', 'Paid plans start with a free 60-day trial. You will not be charged until the trial ends, and only if you provided consent. Cancel anytime.')}
                    </p>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Step 1: Account Information */}
          {step === 1 && (
            <form onSubmit={handleStep1Submit} className="space-y-5">
              <h2 className="text-lg font-semibold text-gray-900">{t('accountInformation')}</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('fullName')} *</label>
                <input
                  type="text"
                  placeholder={t('fullNamePlaceholder')}
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  required
                  data-testid="fullname-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('email')} *</label>
                <input
                  ref={emailInputRef}
                  type="email"
                  placeholder="negocio@email.com"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    if (emailStatus !== 'idle') { setEmailStatus('idle'); setEmailTakenReason(null); }
                  }}
                  onBlur={(e) => checkEmailAvailability(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  required
                  data-testid="email-input"
                />
                {emailStatus === 'checking' && (
                  <p className="text-xs text-slate-500 mt-1" data-testid="signup-email-checking">Checking…</p>
                )}
                {emailStatus === 'taken' && (
                  <EmailTakenNotice
                    reason={emailTakenReason}
                    onChangeEmail={() => {
                      setFormData({ ...formData, email: '' });
                      setEmailStatus('idle');
                      setEmailTakenReason(null);
                      setTimeout(() => emailInputRef.current?.focus(), 0);
                    }}
                    testIdPrefix="signup-email-taken"
                  />
                )}
                {emailStatus === 'available' && (
                  <p className="text-xs text-emerald-700 mt-1" data-testid="signup-email-available">✓ Email available</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('password')} *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('passwordPlaceholder')}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-2.5 pe-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                    required
                    data-testid="password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Referral Code — hidden when the affiliate system is disabled */}
              {flags.affiliatesEnabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('referralCode', 'Referral Code')} ({t('optional', 'optional')})</label>
                  <input
                    type="text"
                    placeholder="e.g. JOHN1A2B"
                    value={referralCode}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase();
                      setReferralCode(val);
                      setReferralValid(null);
                      if (val.length >= 4) {
                        axios.get(`${API}/referral/validate/${val}`).then(res => {
                          if (res.data.valid) setReferralValid(res.data.helperName);
                          else setReferralValid(null);
                        }).catch(() => setReferralValid(null));
                      }
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none font-mono uppercase"
                    data-testid="referral-code-input"
                  />
                  {referralValid && (
                    <p className="mt-1 text-sm text-green-600 flex items-center gap-1" data-testid="referral-valid">
                      <Check className="h-3.5 w-3.5" /> {t('referredBy', 'Referred by')} {referralValid}
                    </p>
                  )}
                </div>
              )}

              {/* Legal consent checkbox */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="business-consent"
                  checked={consentGiven}
                  onChange={(e) => setConsentGiven(e.target.checked)}
                  className="mt-1 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                  data-testid="consent-checkbox"
                />
                <label htmlFor="business-consent" className="text-sm text-gray-600 cursor-pointer">
                  {t('consentText', 'I agree to the')}{' '}
                  <Link to="/terms" target="_blank" className="text-purple-600 hover:text-purple-700 underline font-medium">
                    {t('termsAndConditions', 'Terms & Conditions')}
                  </Link>
                  {', '}
                  <Link to="/privacy" target="_blank" className="text-purple-600 hover:text-purple-700 underline font-medium">
                    {t('privacyPolicyTitle', 'Privacy Policy')}
                  </Link>
                  {' '}{t('consentAnd', 'and')}{' '}
                  <Link to="/disputes" target="_blank" className="text-purple-600 hover:text-purple-700 underline font-medium">
                    {t('disputePolicyTitle', 'Dispute Policy')}
                  </Link>
                </label>
              </div>

              <button
                type="submit"
                disabled={emailStatus === 'taken' || emailStatus === 'checking'}
                className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:bg-purple-300 disabled:cursor-not-allowed"
                data-testid="step1-next-button"
              >
                {emailStatus === 'checking' ? t('checkingEmail', 'Checking email…') : t('continue')}
              </button>
            </form>
          )}

          {/* Step 2: Business Information */}
          {step === 2 && (
            <form onSubmit={handleStep2Submit} className="space-y-5">
              <h2 className="text-lg font-semibold text-gray-900">{t('businessInformation')}</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('businessName')} *</label>
                <input
                  type="text"
                  placeholder={t('businessNamePlaceholder')}
                  value={formData.businessName}
                  onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  required
                  data-testid="business-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('description')} *</label>
                <textarea
                  placeholder={t('tellUsAbout')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none resize-none"
                  rows={4}
                  required
                  data-testid="description-input"
                />
              </div>

              {/* Soft duplicate-listing warning */}
              {duplicateMatches.length > 0 && (
                <div
                  className="rounded-xl border border-amber-300 bg-amber-50 p-4"
                  data-testid="duplicate-listing-warning"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-amber-900 text-sm">
                        {t('duplicateListingHeader', 'This business may already be listed.')}
                      </p>
                      <p className="text-xs text-amber-800/90 mt-0.5">
                        {t('duplicateListingHelp', 'Please claim the existing listing instead of creating a duplicate.')}
                      </p>
                      <div className="mt-3 space-y-2">
                        {duplicateMatches.slice(0, 3).map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => navigate(`/business/${m.id}`)}
                            className="w-full flex items-center justify-between gap-3 rounded-lg bg-white border border-amber-300 px-3 py-2 text-left hover:bg-amber-100 transition-colors"
                            data-testid={`duplicate-match-${m.id}`}
                          >
                            <span className="min-w-0">
                              <span className="block text-sm font-semibold text-gray-900 truncate">{m.businessName}</span>
                              <span className="block text-xs text-gray-500 truncate">
                                {[m.category, m.city].filter(Boolean).join(' — ')}
                              </span>
                            </span>
                            <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                              <ShieldCheck className="h-4 w-4" />
                              {t('duplicateViewBtn', 'View listing')}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {duplicateChecking && duplicateMatches.length === 0 && formData.businessName.length >= 3 && (
                <p className="text-xs text-gray-400 italic" data-testid="duplicate-checking">
                  {t('duplicateChecking', 'Checking for existing listings…')}
                </p>
              )}

              <button type="submit" className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors" data-testid="step2-next-button">
                {t('continue')}
              </button>
            </form>
          )}

          {/* Step 3: Contact & Location */}
          {step === 3 && (
            <form onSubmit={handleStep3Submit} className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('contactLocation')}</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('categoryLabel')} *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                    required
                    data-testid="category-select"
                  >
                    <option value="">{t('selectCategory')}</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {formData.category === 'Other' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('specifyCategory')} *</label>
                    <input
                      type="text"
                      placeholder={t('specifyCategoryPlaceholder')}
                      value={formData.customCategory}
                      onChange={(e) => setFormData({ ...formData, customCategory: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                      data-testid="custom-category-input"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('townCityLabel', 'Town / City')} *</label>
                  <input
                    type="text"
                    placeholder={t('townCityPlaceholder', 'e.g. Madrid')}
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                    required
                    data-testid="city-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('countryLabel', 'Country')} *</label>
                  <select
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white"
                    required
                    data-testid="country-select"
                  >
                    <option value="">{t('countrySelect', 'Select a country')}</option>
                    {countries.map((c) => (
                      <option key={c.code} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('regionLabel2', 'Region / State')}</label>
                  <input
                    type="text"
                    placeholder={t('regionPlaceholder', 'e.g. Andalucía')}
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                    data-testid="region-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('provinceLabel', 'Province')}</label>
                  <input
                    type="text"
                    placeholder={t('provincePlaceholder', 'e.g. Almería')}
                    value={formData.province}
                    onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                    data-testid="province-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('localAreaLabel', 'Local area / District')}</label>
                  <input
                    type="text"
                    value={formData.localArea}
                    onChange={(e) => setFormData({ ...formData, localArea: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                    data-testid="local-area-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('postcodeLabel', 'Postcode / ZIP')}</label>
                  <input
                    type="text"
                    value={formData.postcode}
                    onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                    data-testid="postcode-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('businessAddress')} *</label>
                <input
                  type="text"
                  placeholder={t('addressPlaceholder')}
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  required
                  data-testid="address-input"
                />
                <div className="flex items-start gap-2 mt-2 p-2 bg-amber-50 rounded-lg">
                  <Lightbulb className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">{t('addressTip')}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('phoneNumber')} *</label>
                  <input
                    type="tel"
                    placeholder="+34 123 456 789"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                    required
                    data-testid="phone-input"
                  />
                  {formData.phone && (() => {
                    const iso = (countries.find(c => c.name === formData.country) || {}).code;
                    const { valid, parsed } = formatPhone(formData.phone, iso);
                    return parsed && !valid ? (
                      <p className="text-xs text-amber-600 mt-1" data-testid="phone-hint">
                        {t('phoneHint', 'This number may be incomplete for the selected country. You can still continue.')}
                      </p>
                    ) : null;
                  })()}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('businessEmailLabel')} *</label>
                  <input
                    type="email"
                    placeholder="contacto@tunegocio.com"
                    value={formData.businessEmail}
                    onChange={(e) => setFormData({ ...formData, businessEmail: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                    required
                    data-testid="business-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('websiteLabel')}</label>
                <input
                  type="text"
                  placeholder="www.tunegocio.com"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  data-testid="website-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('openingHoursLabel')}</label>
                <OpeningHoursInput value={formData.openingHours} onChange={(value) => setFormData({ ...formData, openingHours: value })} />
              </div>

              <button type="submit" className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors" data-testid="step3-next-button">
                {t('continue')}
              </button>
            </form>
          )}

          {/* Step 4: Business Advertising Features */}
          {step === 4 && (
            <form onSubmit={handleStep4Submit} className="space-y-5">
              <h2 className="text-lg font-semibold text-gray-900">{t('whatDoesOffer')}</h2>
              <p className="text-sm text-gray-600 -mt-3">{t('selectAllApply')}</p>

              {/* Group Size Accommodation */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">{t('groupSizeLabel')}</label>
                <p className="text-xs text-gray-500 mb-3">{t('groupSizeHelp')}</p>
                <div className="flex flex-wrap gap-2">
                  {filterOptions.groupSizes?.map((size) => renderTagButton('groupSizes', size))}
                </div>
              </div>

              {/* Event Types */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">{t('eventTypesLabel')}</label>
                <p className="text-xs text-gray-500 mb-3">{t('eventTypesHelp')}</p>
                <div className="flex flex-wrap gap-2">
                  {filterOptions.eventTypes?.map((type) => renderTagButton('eventTypes', type))}
                </div>
                {/* Show custom input when "Other" is selected */}
                {formData.eventTypes.includes('Other') && (
                  <div className="mt-3">
                    <input
                      type="text"
                      placeholder={t('specifyEventType')}
                      value={formData.customEventType}
                      onChange={(e) => setFormData({ ...formData, customEventType: e.target.value })}
                      className="w-full px-4 py-2.5 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-purple-50"
                      data-testid="custom-event-type-input"
                    />
                  </div>
                )}
              </div>

              {/* Venue Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">{t('venueTypeLabel')}</label>
                <p className="text-xs text-gray-500 mb-3">{t('venueTypeHelp')}</p>
                <div className="flex flex-wrap gap-2">
                  {filterOptions.venueTypes?.map((type) => renderTagButton('venueType', type))}
                </div>
              </div>

              {/* Dietary Options (if food related) */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">{t('dietaryOptionsLabel')}</label>
                <p className="text-xs text-gray-500 mb-3">{t('dietaryOptionsHelp')}</p>
                <div className="flex flex-wrap gap-2">
                  {filterOptions.dietaryOptions?.map((option) => renderTagButton('dietaryOptions', option))}
                </div>
              </div>

              <button type="submit" className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors" data-testid="step4-next-button">
                {t('continue')}
              </button>
            </form>
          )}

          {/* Step 5: Additional Features */}
          {step === 5 && (
            <form onSubmit={handleStep5Submit} className="space-y-5">
              <h2 className="text-lg font-semibold text-gray-900">{t('additionalFeaturesTitle')}</h2>

              {/* Additional Features */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-3">{t('specialFeatures')}</label>
                <div className="space-y-3">
                  {filterOptions.additionalFeatures?.map((feature) => (
                    <label key={feature} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <span className="text-sm text-gray-700">{tf(t, feature)}</span>
                      <button
                        type="button"
                        onClick={() => toggleArrayField('additionalFeatures', feature)}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          isSelected('additionalFeatures', feature) ? 'bg-purple-600' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                          isSelected('additionalFeatures', feature) ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </label>
                  ))}
                  {formData.additionalFeatures.includes('Other') && (
                    <div className="mt-2">
                      <input
                        type="text"
                        placeholder={t('specifyFeature', 'Describe your feature/service...')}
                        value={formData.customFeatureText}
                        onChange={(e) => setFormData({ ...formData, customFeatureText: e.target.value })}
                        className="w-full px-4 py-2.5 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-purple-50"
                        data-testid="custom-feature-input"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Accessibility & Facilities */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-3">{t('accessibilityFacilities')}</label>
                <div className="space-y-3">
                  {filterOptions.accessibilityFeatures?.map((feature) => (
                    <label key={feature} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <span className="text-sm text-gray-700">{tf(t, feature)}</span>
                      <button
                        type="button"
                        onClick={() => toggleArrayField('accessibilityFeatures', feature)}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          isSelected('accessibilityFeatures', feature) ? 'bg-purple-600' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                          isSelected('accessibilityFeatures', feature) ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </label>
                  ))}
                </div>
              </div>

              <button type="submit" className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors" data-testid="step5-next-button">
                {t('continue')}
              </button>
            </form>
          )}

          {/* Step 6: Founding Business Offer — 12 months free, no card */}
          {step === 6 && (
            <form onSubmit={handleFinalSubmit} className="space-y-5">
              {/* Plan summary banner — adapts to the selected membership */}
              {isFreePlan ? (
                <div className="rounded-2xl border-2 border-gray-300 bg-gray-50 p-5" data-testid="plan-summary-free">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1">{t('membership.freeBadge', 'Free Directory')}</p>
                  <h2 className="text-xl font-bold text-gray-900 leading-tight">{t('membership.freeTitle', 'Free listing — €0 forever')}</h2>
                  <p className="text-sm text-gray-700 mt-2 leading-relaxed">{t('membership.freeBody', 'Your basic listing (business name, category and town) goes live once approved. No card required. You can upgrade to Basic or Business Plus anytime to show your full details.')}</p>
                  <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm text-gray-800">
                    {(planFeatures('free') || selectedTier?.publicFeatures || ['Business name', 'Business category', 'Town', 'Basic directory visibility']).map((f) => (
                      <li key={f} className="flex items-start gap-1.5"><span className="text-purple-600 font-bold">✓</span>{f}</li>
                    ))}
                  </ul>
                </div>
              ) : isPaidPlan ? (
                <div className="rounded-2xl border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-fuchsia-50 p-5" data-testid="plan-summary-paid">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-purple-700 mb-1">{formData.planId === 'basic_monthly' ? t('membership.basicName', 'Basic') : t('membership.plusName', 'Business Plus')}</p>
                  <h2 className="text-xl font-bold text-gray-900 leading-tight">
                    {fmtPrice(selectedTier?.amount)}<span className="text-sm font-normal text-gray-600">/{selectedTier?.interval === 'year' ? t('membership.year', 'year') : t('membership.month', 'month')} · {t('membership.ivaIncluded', 'IVA included')}</span>
                  </h2>
                  <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
                    {t('membership.trial60', 'First 60 days free')}
                  </div>
                  <p className="text-sm text-gray-700 mt-3 leading-relaxed">
                    {t('membership.paidBody', 'Your listing goes live once approved. You will NOT be charged for the first 60 days. After the free trial ends, your card is charged automatically only if you consented — and you can cancel anytime before then.')}
                  </p>
                  <p className="text-xs text-gray-600 mt-2">
                    {t('membership.firstChargeOn', 'First charge (if you continue):')} <span className="font-semibold">{new Date(Date.now() + 60 * 86400000).toLocaleDateString('es-ES')}</span> · {fmtPrice(selectedTier?.amount)}/{selectedTier?.interval === 'year' ? t('membership.year', 'year') : t('membership.month', 'month')}
                  </p>
                  <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm text-gray-800">
                    {(planFeatures(tierKeyFor(formData.planId)) || selectedTier?.publicFeatures || []).map((f) => (
                      <li key={f} className="flex items-start gap-1.5"><span className="text-purple-600 font-bold">✓</span>{f}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="rounded-2xl border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-fuchsia-50 p-5" data-testid="founders-offer-banner">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-purple-700 mb-1">
                    {t('founders.badge', 'Founding Business Offer')}
                  </p>
                  <h2 className="text-xl font-bold text-gray-900 leading-tight">
                    {t('founders.title', '12 Months Free — Full Access')}
                  </h2>
                  <p className="text-sm text-gray-700 mt-2 leading-relaxed">
                    {t('founders.body', "Your listing goes live as soon as your registration is approved — no card required. You get the full Find-2-Day toolkit for 12 months from your signup date.")}
                  </p>
                  <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm text-gray-800">
                    <li className="flex items-start gap-1.5"><span className="text-purple-600 font-bold">✓</span>{t('founders.featListing', 'Public listing + photos')}</li>
                    <li className="flex items-start gap-1.5"><span className="text-purple-600 font-bold">✓</span>{t('founders.featEvents', 'Publish events')}</li>
                    <li className="flex items-start gap-1.5"><span className="text-purple-600 font-bold">✓</span>{t('founders.featOffers', 'Publish offers')}</li>
                    <li className="flex items-start gap-1.5"><span className="text-purple-600 font-bold">✓</span>{t('founders.featAmenities', 'Amenities & accessibility')}</li>
                    <li className="flex items-start gap-1.5"><span className="text-purple-600 font-bold">✓</span>{t('founders.featBoost', 'Use paid boosts to reach more locals')}</li>
                    <li className="flex items-start gap-1.5"><span className="text-purple-600 font-bold">✓</span>{t('founders.featNoCard', 'No card at signup')}</li>
                  </ul>
                </div>
              )}

              {/* Paid checkout not yet enabled in Preview (no Stripe test keys) */}
              {isPaidPlan && paidNotReady && (
                <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="paid-checkout-not-ready">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-900">
                      {t('membership.checkoutNotReady', 'Secure card checkout for paid plans will be switched on in the next step (Stripe Test mode). Your registration details are complete and ready — no charge has been made.')}
                    </p>
                  </div>
                </div>
              )}

              {/* EU Consumer Law — Immediate service start waiver (required) */}
              <div className="border-2 border-amber-300 bg-amber-50 rounded-xl p-4" data-testid="waiver-box">
                <label htmlFor="withdrawal-waiver" className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    id="withdrawal-waiver"
                    checked={waiverAccepted}
                    onChange={(e) => setWaiverAccepted(e.target.checked)}
                    className="mt-1 h-5 w-5 text-purple-600 border-amber-400 rounded focus:ring-purple-500 cursor-pointer flex-shrink-0"
                    data-testid="waiver-checkbox"
                    required
                  />
                  <span className="text-sm text-amber-900">
                    <span className="font-semibold block mb-1">{t('waiverTitle', 'Immediate listing activation and acknowledgement')}</span>
                    {t('waiverLabel', 'By ticking this box, I request that my business listing be activated immediately once approved. I understand that once my listing becomes publicly visible on Find-2-Day, the digital service is considered delivered under EU Directive 2011/83/EU Art. 16(m).')}
                    <span className="block mt-1 text-xs text-amber-700">
                      {t('waiverLegalNote', 'EU Directive 2011/83/EU, Art. 16(m) — digital service supplied immediately on request.')}
                    </span>
                  </span>
                </label>
              </div>

              {/* Iter 192 — Hard duplicate-listing block (server-side 409 retry) */}
              {duplicateBlock && (
                <div
                  className="rounded-xl border-2 border-red-300 bg-red-50 p-4"
                  data-testid="duplicate-block-warning"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-red-900 text-sm">
                        {t('duplicateBlockHeader', 'A similar listing already exists')}
                      </p>
                      <p className="text-xs text-red-800/90 mt-1">
                        {t('duplicateBlockHelp2', 'A similar listing already exists. If this is genuinely a different business, you can confirm and continue.')}
                      </p>
                      {duplicateBlock.match && (
                        <button
                          type="button"
                          onClick={() => navigate(`/business/${duplicateBlock.match.id}`)}
                          className="mt-3 w-full flex items-center justify-between gap-3 rounded-lg bg-white border border-red-300 px-3 py-2 text-left hover:bg-red-100 transition-colors"
                          data-testid={`duplicate-block-match-${duplicateBlock.match.id}`}
                        >
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-gray-900 truncate">{duplicateBlock.match.businessName}</span>
                            <span className="block text-xs text-gray-500 truncate">
                              {[duplicateBlock.match.category, duplicateBlock.match.city].filter(Boolean).join(' — ')}
                            </span>
                          </span>
                          <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                            <ShieldCheck className="h-4 w-4" />
                            {t('duplicateViewBtn', 'View listing')}
                          </span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(ev) => handleFinalSubmit(ev, { confirmedNoDuplicate: true })}
                        disabled={loading}
                        className="mt-3 w-full py-2.5 bg-white border-2 border-red-400 text-red-800 font-medium rounded-lg hover:bg-red-100 transition-colors text-sm disabled:opacity-50"
                        data-testid="duplicate-block-confirm-different"
                      >
                        {loading ? t('settingUp') : t('duplicateBlockConfirmDifferent', 'This is a different business — continue anyway')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !waiverAccepted}
                className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="final-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {t('settingUp')}
                  </>
                ) : (
                  isFreePlan
                    ? t('membership.freeCta', 'Create my free listing')
                    : isPaidPlan
                      ? t('membership.paidCta', 'Continue — start my 60-day free trial')
                      : t('founders.submitCta', 'Activate my free 12 months')
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-2" data-testid="contact-support-before-chargeback-note">
                {t('founders.supportNote', 'Questions? Email')}{' '}
                <a href="mailto:support@find-2-day.com" className="text-purple-600 underline">support@find-2-day.com</a>
              </p>

              {/* Preview before submit */}
              <button
                type="button"
                onClick={() => openSignupPreview(formData, formData.planId || 'founders')}
                className="w-full mt-2 py-2.5 bg-white border border-purple-300 text-purple-700 font-medium rounded-lg hover:bg-purple-50 transition-colors flex items-center justify-center gap-2 text-sm"
                data-testid="signup-preview-btn"
              >
                <Eye className="h-4 w-4" />
                {t('previewYourListing', 'Preview your listing')}
              </button>
              <p className="text-xs text-gray-500 text-center mt-1" data-testid="signup-preview-hint">
                {t('previewYourListingHint', 'See exactly how your listing will look before you submit. Opens in a new tab — nothing is saved yet.')}
              </p>
            </form>
          )}

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-600">
            {t('alreadyHaveAccount')}{' '}
            <Link to="/signin" className="text-purple-600 hover:text-purple-700 font-semibold">
              {t('signIn')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessSignUp;
